import { getHeaders } from './authentication'
import { Organisation, OrganisationCounts } from '../types/organisation'
import { Pharmacy } from '../types/pharmacy'

interface State {
  organisations: Organisation[]
  organisation: null | Organisation
  organisationCounts: OrganisationCounts
  branches: Pharmacy[]
  whiteLabelApp: any,
  whiteLabelApps: any[]
  documents?: any[]
  sales?: any[]
  page: number
  rowsPerPage: number
  totalCount: null
  showTab: boolean
  showFilterIcon?: boolean
  filter?: {
    startDate?: 'desc' | 'asc',
    commissionable?: boolean,
    type?: string,
    payment?: string,
    plan?: string
  },
  flow?: null | string
  type: string
  tabValue: "website" | "account" | "plan" | "branding" | "branches" | "feature_flags" | "documents"
  documentData: any[]
  message: null | string
  whiteLabelMessage: null | string
  fetching: boolean
  searchResults: null | {
    pharmacies: Pharmacy[],
    organisations: Organisation[]
  },
  statusActiveFilter: boolean,
  patientsReportInit:{
    fetching: boolean,
    data: string | null,
    error: any
  }
}

// GLOBALS
// Initial State
let initialState: State = {
  organisation: null,
  organisations: [],
  organisationCounts: {
    active: 0,
    inactive: 0,
    actionNeeded: 0,
    paymentOverdue: 0,
  },
  branches: [],
  whiteLabelApp: null,
  whiteLabelMessage: null,
  whiteLabelApps: [],
  searchResults: null,
  page: 0,
  rowsPerPage: 10,
  totalCount: null,
  showTab: false,
  type: 'active',
  tabValue: "website",
  documentData: [],
  message: null,
  fetching: false,
  filter: { startDate: 'desc' },
  statusActiveFilter: true,
  patientsReportInit: { fetching: false, error: null, data: null }

};

// Action creators

const ajaxActionFactory = (verb: string, url: string, method: string, data?: any, callback?) => {
  return ({
    types: [verb, `${verb}_SUCCESS`, `${verb}_FAILURE`],
    payload: {
      request: {
        url, method, headers: getHeaders(), data
      }
    },
    callback
  })
}

export const assertSucceeded = (action) => {
  if (action?.type?.endsWith('_FAILURE')) throw action.error ?? new Error(action.type)
  return action
}

interface SearchPayload {
  searchTerm: string;
  isActive: boolean;
  searchBy?: 'orgs' | 'pharmacies';
  type?: 'prescriber';
}

export const search = ({ searchTerm, isActive, searchBy, type }: SearchPayload) => {
  const queryParams = new URLSearchParams({ searchTerm });

  if (typeof isActive === 'boolean') {
    queryParams.append('isActive', String(isActive));
  }

  if (searchBy) {
    queryParams.append('searchBy', searchBy);
  }

  if (type) {
    queryParams.append('type', type);
  }

  const url = `/chains/search?${queryParams.toString()}`;

  return ajaxActionFactory(`SEARCH`, url, 'GET')
}

export const createOrganisation = (data) => ajaxActionFactory(`CREATE_ORGANISATION`, `/chains`, 'POST', data)
export const inviteToRegisterWithStripe = (orgID, data) => ajaxActionFactory(`INVITE_ORGANISATION_TO_STRIPE`, `/chains/invite/${orgID}`, 'POST', data);
export const inviteToRegisterWithWebkul = (orgID) => ajaxActionFactory(`INVITE_ORGANISATION_TO_WEBKUL`, `/chains/${orgID}/create-otc-seller`, 'PUT');
export const resendOTCSellerEmail = (orgID) => ajaxActionFactory(`RESEND_OTC_SELLER_EMAIL`, `/chains/${orgID}/resend-otc-seller-email`, 'GET');
export const activatePharmaciesForMobilePayment = (orgID, data) => ajaxActionFactory(`ACTIVATE_PHARMACIES_FOR_MOBILE_PAYMENT`, `/chains/${orgID}/activate-payment`, 'PUT', data);
export const updateOrganisation = (orgID, data) => ajaxActionFactory(`UPDATE_ORGANISATION`, `/chains/${orgID}`, 'PUT', data)
export const updateActiveStatus = (orgID, data) => ajaxActionFactory(`UPDATE_ACTIVE_STATUS`, `/chains/${orgID}/update-active-status`, 'PUT', data);
export const createCharge = (orgID, data) => ajaxActionFactory('CREATE_CHARGE_ON_STRIPE', `/chains/${orgID}/create-charge`, 'POST', data);

export const getOrganisations = (name, page = 0, type, filter) => {
  const resourceRoute = '/chains';

  if (name) {
    const searchParams = new URLSearchParams({ name });
    const url = `${resourceRoute}?${searchParams.toString()}`;
    return ajaxActionFactory(`GET_ORGANISATIONS`, url, 'GET');
  }

  const queryParams = new URLSearchParams({ type, page: String(page) });
  if (filter?.commissionable !== undefined) queryParams.append('commissionable', String(filter.commissionable));
  if (filter?.payment !== undefined) queryParams.append('payment', filter.payment);
  if (filter?.startDate !== undefined) queryParams.append('contractStartDate', filter.startDate);
  if (filter?.plan !== undefined) queryParams.append('plan', filter.plan);
  
  const url = `${resourceRoute}?${queryParams.toString()}`;
  return ajaxActionFactory(`GET_ORGANISATIONS`, url, 'GET');
};

export const getOrganisationCountsByType = (filter) => {
  const searchParams = new URLSearchParams();
  if (filter?.commissionable !== undefined) searchParams.append('commissionable', String(filter.commissionable));
  if (filter?.payment !== undefined) searchParams.append('payment', filter.payment);

  const query = searchParams.toString();
  const url = ['/chains/counts', query].filter(Boolean).join('?');

  return ajaxActionFactory(`GET_ORGANISATION_COUNTS`, url, 'GET');
};

export const getOrganisation = (orgID) => ajaxActionFactory(`GET_ORGANISATION`, `/chains/${orgID}`, 'GET')
export const getBranches = (orgID) => ajaxActionFactory(`GET_BRANCHES`, `pharmacies/V2?organisationID=${orgID}`, 'GET')


export const resetPagination = (page = 0) => {
  return { type: `RESET_PAGE`, payload: { page } }
}

export const setFilter = (filter) => {
  return { type: 'SET_FILTER', payload: filter }
}

export const resetSearchResults = () => {
  return { type: `RESET_SEARCH_RESULTS` }
}

export const showTabIcon = (value) => {
  return { type: `SHOW_TAB_ICON`, payload: { value } }
}

export const changeTab = (value) => {
  return ({ type: `CHANGE_TAB`, payload: { value } })
}

export const changeType = (value) => {
  return ({ type: `CHANGE_TYPE`, payload: { value } })
}

export const resetError = () => {
  return { type: `RESET_ERROR` }
}

export const setStatusActiveFilter = (status) => {
  return { type: `SET_STATUS_ACTIVE_FILTER`, payload: status }
}

export const getPaymentReports = (orgID) => {
  return ajaxActionFactory('GET_ORG_PAYMENT_REPORTS', `/chains/${orgID}/payment-reports`, 'GET');
}

export const getPaymentReportDownloadLink = ({ fileName, orgID }, cb) => {
  return ajaxActionFactory('GET_ORG_PAYMENT_REPORT', `/chains/${orgID}/payment-reports/${fileName}`, 'GET', null, cb);
}

export const getSalesReports = (orgID) => {
  return ajaxActionFactory('GET_ORG_SALES_REPORTS', `/chains/${orgID}/sales-reports`, 'GET');
};

export const getSalesReportDownloadLink = ({ fileName, orgID }, cb) => {
  return ajaxActionFactory('GET_ORG_SALES_REPORT', `/chains/${orgID}/sales-reports/${fileName}`, 'GET', null, cb);
};

// GET White Label Apps
const GET_WHITE_LABEL_APPS = 'GET_WHITE_LABEL_APPS';
const GET_WHITE_LABEL_APPS_SUCCESS = 'GET_WHITE_LABEL_APPS_SUCCESS';
const GET_WHITE_LABEL_APPS_FAILURE = 'GET_WHITE_LABEL_APPS_FAILURE';
// GET White Label App
const GET_WHITE_LABEL_APP = 'GET_WHITE_LABEL_APP';
const GET_WHITE_LABEL_APP_SUCCESS = 'GET_WHITE_LABEL_APP_SUCCESS';
const GET_WHITE_LABEL_APP_FAILURE = 'GET_WHITE_LABEL_APP_FAILURE';
// PUT White Label App
const PUT_WHITE_LABEL_APP = 'PUT_WHITE_LABEL_APP';
const PUT_WHITE_LABEL_APP_SUCCESS = 'PUT_WHITE_LABEL_APP_SUCCESS';
const PUT_WHITE_LABEL_APP_FAILURE = 'PUT_WHITE_LABEL_APP_FAILURE';
// POST White Label Banner
const POST_WHITE_LABEL_BANNER = 'POST_WHITE_LABEL_BANNER';
const POST_WHITE_LABEL_BANNER_SUCCESS = 'POST_WHITE_LABEL_BANNER_SUCCESS';
const POST_WHITE_LABEL_BANNER_FAILURE = 'POST_WHITE_LABEL_BANNER_FAILURE';
// PUT White Label Banner
const PUT_WHITE_LABEL_BANNER = 'PUT_WHITE_LABEL_BANNER';
const PUT_WHITE_LABEL_BANNER_SUCCESS = 'PUT_WHITE_LABEL_BANNER_SUCCESS';
const PUT_WHITE_LABEL_BANNER_FAILURE = 'PUT_WHITE_LABEL_BANNER_FAILURE';
// DELETE White Label Banner
const DELETE_WHITE_LABEL_BANNER = 'DELETE_WHITE_LABEL_BANNER';
const DELETE_WHITE_LABEL_BANNER_SUCCESS = 'DELETE_WHITE_LABEL_BANNER_SUCCESS';
const DELETE_WHITE_LABEL_BANNER_FAILURE = 'DELETE_WHITE_LABEL_BANNER_FAILURE';
// POST Request Pro status activation
const POST_REQUEST_PRO_STATUS_ACTIVATION = 'POST_REQUEST_PRO_STATUS_ACTIVATION';
const POST_REQUEST_PRO_STATUS_ACTIVATION_SUCCESS = 'POST_REQUEST_PRO_STATUS_ACTIVATION_SUCCESS';
const POST_REQUEST_PRO_STATUS_ACTIVATION_FAILURE = 'POST_REQUEST_PRO_STATUS_ACTIVATION_FAILURE';

export const requestProStatusActivation = (chainId) => ajaxActionFactory(
  POST_REQUEST_PRO_STATUS_ACTIVATION,
  `/chains/${chainId}/request-pro-activation`,
  'POST',
);

export const getWhiteLabelApps = () => ajaxActionFactory(GET_WHITE_LABEL_APPS, '/whitelabel_applications', 'GET');

export const getWhiteLabelApp = id => ajaxActionFactory(GET_WHITE_LABEL_APP, `/whitelabel_applications/${id}`, 'GET');
export const putWhiteLabelApp = (id, data) => ajaxActionFactory(GET_WHITE_LABEL_APP, `/whitelabel_applications/${id}`, 'PUT', data);

export const addWhiteLabelBanner = (id, data) => ajaxActionFactory(
  POST_WHITE_LABEL_BANNER,
  `/whitelabel_applications/${id}/banners`,
  'POST',
  data,
);

export const editWhiteLabelBanner = (id, bannerId, data) => ajaxActionFactory(
  PUT_WHITE_LABEL_BANNER,
  `/whitelabel_applications/${id}/banners/${bannerId}`,
  'PUT',
  data,
);

export const deleteWhiteLabelBanner = (id, bannerId) => ajaxActionFactory(
  DELETE_WHITE_LABEL_BANNER,
  `/whitelabel_applications/${id}/banners/${bannerId}`,
  'DELETE',
  null,
  (err) => console.log('err', err)
);

const GET_PATIENTS_REPORT_INIT = 'GET_PATIENTS_REPORT_INIT';
const GET_PATIENTS_REPORT_INIT_SUCCESS = 'GET_PATIENTS_REPORT_INIT_SUCCESS';
const GET_PATIENTS_REPORT_INIT_FAILURE = 'GET_PATIENTS_REPORT_INIT_FAILURE';
const GET_PATIENTS_REPORT_INIT_RESET = 'GET_PATIENTS_REPORT_INIT_RESET';
const GET_PATIENTS_REPORT_INIT_INTERNAL_ERROR = 'GET_PATIENTS_REPORT_INIT_INTERNAL_ERROR';

export const getPatientsReportInit = (orgID) => {
  return ajaxActionFactory(GET_PATIENTS_REPORT_INIT, `/chains/${orgID}/patients`, 'GET');
}

export const getPatientsInternalError = (errorMessage) => {
  return { type: GET_PATIENTS_REPORT_INIT_INTERNAL_ERROR, payload: errorMessage };
};

export const resetPatientsReportAction = () => ({
  type: GET_PATIENTS_REPORT_INIT_RESET
});

export const resetOrganisationsListAction = () => ({
  type: 'GET_ORGANISATIONS_RESET'
});

const organisationsReducer = (state: State = initialState, action): State => {
  switch (action.type) {
    case `SEARCH`:
      return { ...state }
    case `SEARCH_SUCCESS`:
      return { ...state, searchResults: action.payload.data }
    case `SEARCH_FAILURE`:
      return { ...state, searchResults: null }
    case `RESET_SEARCH_RESULTS`:
      return { ...state, searchResults: null }
    case `INVITE_ORGANISATION_TO_STRIPE`:
      return { ...state }
    case `INVITE_ORGANISATION_TO_STRIPE_SUCCESS`:
      return { ...state, message: 'An email has been sent to the Account Administrator' }
    case `INVITE_ORGANISATION_TO_STRIPE_FAILURE`:
      return { ...state, message: action.error?.response?.data?.error }
    case `CREATE_CHARGE_ON_STRIPE`:
      return { ...state }
    case `CREATE_CHARGE_ON_STRIPE_SUCCESS`:
      return { ...state, message: 'A charge has been created, please check the Stripe Dashboard' }
    case `CREATE_CHARGE_ON_STRIPE_FAILURE`:
      return { ...state, message: action.error?.response?.data?.error }
      case `INVITE_ORGANISATION_TO_WEBKUL`:
        return { ...state }
      case `INVITE_ORGANISATION_TO_WEBKUL_SUCCESS`:
        return { ...state, message: 'An email has been sent to the Account Administrator' }
      case `INVITE_ORGANISATION_TO_WEBKUL_FAILURE`:
        return { ...state, message: action.error?.response?.data?.error }
    case `RESEND_OTC_SELLER_EMAIL`:
        return { ...state }
      case `RESEND_OTC_SELLER_EMAIL_SUCCESS`:
        return { ...state, message: 'An email has been resent to the Account Administrator' }
      case `RESEND_OTC_SELLER_EMAIL_FAILURE`:
        return { ...state, message: action.error?.response?.data?.error }
    case `ACTIVATE_PHARMACIES_FOR_MOBILE_PAYMENT`:
      return { ...state }
    case `ACTIVATE_PHARMACIES_FOR_MOBILE_PAYMENT_SUCCESS`:
      return { ...state, message: 'All pharmacies have been updated' }
    case `ACTIVATE_PHARMACIES_FOR_MOBILE_PAYMENT_FAILURE`:
      return { ...state, message: action.error }
    case `RESET_ERROR`:
      return {
        ...state, message: null
      }
    case `SET_STATUS_ACTIVE_FILTER`:
      return { ...state, statusActiveFilter: action.payload }
    case 'SET_FILTER':
      return {
        ...state,
        filter: { ...state.filter, ...action.payload }
      }
    case `DELETE_ORGANISATION`:
      return {
        ...state
      }
    case `CREATE_ORGANISATION`:
      return {
        ...state, fetching: true
      }
    case `CREATE_ORGANISATION_SUCCESS`:
      return {
        ...state, fetching: false, message: 'Organisation Created Successfully'
      }
    case `CREATE_ORGANISATION_FAILURE`:
      return {
        ...state,
        fetching: false, message: 'Problem creating organisation'
      }
    case POST_WHITE_LABEL_BANNER:
    case `UPDATE_ORGANISATION`:
      return {
        ...state, fetching: true, message: null
      };
    case PUT_WHITE_LABEL_APP:
    case PUT_WHITE_LABEL_BANNER:
    case DELETE_WHITE_LABEL_BANNER:
      return {
        ...state, fetching: true, whiteLabelMessage: null
      };
    case POST_REQUEST_PRO_STATUS_ACTIVATION_SUCCESS:
      return {
        ...state, fetching: false, message: 'Activation email sent to organisation.'
      };
    case POST_REQUEST_PRO_STATUS_ACTIVATION_FAILURE:
      return {
        ...state, fetching: false, message: action?.error?.response?.data?.error?.text || action.error,
      };
    case `UPDATE_ORGANISATION_SUCCESS`:
      return {
        ...state, fetching: false, message: 'Updated Successfully'
      };
    case PUT_WHITE_LABEL_APP_SUCCESS:
    case PUT_WHITE_LABEL_BANNER_SUCCESS:
      return {
        ...state, fetching: false, whiteLabelMessage: 'Updated Successfully'
      };
    case POST_WHITE_LABEL_BANNER_SUCCESS:
      return {
        ...state, fetching: false, whiteLabelMessage: 'Banner Added Successfully'
      };
    case DELETE_WHITE_LABEL_BANNER_SUCCESS:
      return {
        ...state, fetching: false, whiteLabelMessage: 'Banner Deleted Successfully'
      };
    case PUT_WHITE_LABEL_APP_FAILURE:
    case PUT_WHITE_LABEL_BANNER_FAILURE:
    case POST_WHITE_LABEL_BANNER_FAILURE:
    case DELETE_WHITE_LABEL_BANNER_FAILURE:
      return {
        ...state,
        fetching: false, whiteLabelMessage: action?.error?.response?.data?.error?.text || action.error,
      };
    case `UPDATE_ORGANISATION_FAILURE`:
      return {
        ...state,
        fetching: false, message: action?.error?.response?.data?.error?.text || action.error
      }
    case `GET_ORGANISATIONS`:
      return {
        ...state,
        organisations: [], message: null, searchResults: null,
        fetching: true
      }
    case `GET_ORGANISATIONS_SUCCESS`:
      return {
        ...state,
        fetching: false,
        organisations: action.payload.data.data,
        totalCount: action.payload.data.totalCount
      }
    case `GET_ORGANISATIONS_FAILURE`:
      return {
        ...state,
        organisations: [],
        fetching: false,
        message: action.error.response ? action.error.response.data.error : action.error
      }
    case `GET_ORGANISATION_COUNTS`:
      return {
        ...state,
        organisationCounts: {
          active: 0,
          inactive: 0,
          actionNeeded: 0,
          paymentOverdue: 0,
        },
        message: null,
        fetching: true,
      };
    case `GET_ORGANISATION_COUNTS_SUCCESS`:
      return {
        ...state,
        fetching: false,
        organisationCounts: action.payload.data,
      };
    case `GET_ORGANISATION_COUNTS_FAILURE`:
      return {
        ...state,
        organisationCounts: {
          active: 0,
          inactive: 0,
          actionNeeded: 0,
          paymentOverdue: 0,
        },
        fetching: false,
        message: action.error.response ? action.error.response.data.error : action.error
      };
    case `UPDATE_ACTIVE_STATUS`:
      return {
        ...state, fetching: true, message: null
      }
    case `UPDATE_ACTIVE_STATUS_SUCCESS`:
      return {
        ...state, fetching: false, message: 'Updated Successfully'
      }
    case `UPDATE_ACTIVE_STATUS_FAILURE`:
      return {
        ...state,
        fetching: false, message: action?.error?.response?.data?.error?.text || action.error
      }
    case `GET_ORGANISATIONS_RESET`:
      return {
        ...state,
        organisations: [],
        fetching: false,
      }
    case `GET_ORGANISATION`:
      return {
        ...state,
        showFilterIcon: false,
        fetching: true
      }
    case `GET_ORGANISATION_SUCCESS`:
      return {
        ...state,
        fetching: false,
        organisation: action.payload.data.data[0]
      }
    case `GET_ORGANISATION_FAILURE`:
      return {
        ...state,
        message: action.error,
        fetching: false
      }
    case GET_WHITE_LABEL_APPS:
      return {
        ...state,
        whiteLabelApps: [],
        fetching: true,
      };
    case GET_WHITE_LABEL_APPS_SUCCESS:
      return {
        ...state,
        whiteLabelApps: action.payload.data.data,
        fetching: false,
      };
    case GET_WHITE_LABEL_APP:
      return {
        ...state,
        whiteLabelApp: [],
        fetching: true,
      };
    case GET_WHITE_LABEL_APP_SUCCESS:
      return {
        ...state,
        whiteLabelApp: action.payload.data.data,
        fetching: false,
      };
    case GET_WHITE_LABEL_APPS_FAILURE:
    case GET_WHITE_LABEL_APP_FAILURE:
      return {
        ...state,
        message: action?.error?.response?.data?.error?.text || action.error,
        fetching: false,
      };
    case `GET_BRANCHES`:
      return {
        ...state,
        branches: [],
        fetching: true
      }
    case `GET_BRANCHES_SUCCESS`:
      return {
        ...state,
        fetching: false,
        branches: action.payload.data.data
      }
    case `GET_BRANCHES_FAILURE`:
      return {
        ...state,
        branches: [],
        fetching: false,
        message: action.error
      }
    case `RESET_PAGE`:
      return { ...state, page: action.payload.page }
    case `SHOW_TAB_ICON`:
      return { ...state, showTab: action.payload.value }
    case `CHANGE_TAB`:
      return { ...state, tabValue: action.payload.value }
    case `CHANGE_TYPE`:
      return { ...state, type: action.payload.value }
    case 'GET_ORG_SALES_REPORTS':
    case 'GET_ORG_PAYMENT_REPORTS':
      return { ...state, fetching: true, message: null };
    case 'GET_ORG_PAYMENT_REPORTS_SUCCESS':
      return { ...state, fetching: false, documents: action.payload.data };
    case 'GET_ORG_SALES_REPORTS_SUCCESS':
      return { ...state, fetching: false, sales: action.payload.data };
    case 'GET_ORG_SALES_REPORTS_FAILURE':
    case 'GET_ORG_PAYMENT_REPORTS_FAILURE':
      return { ...state, fetching: false, message: action.error };
    case 'GET_ORG_SALES_REPORT':
    case 'GET_ORG_PAYMENT_REPORT':
      return { ...state, fetching: true };
    case 'GET_ORG_SALES_REPORT_SUCCESS':
    case 'GET_ORG_PAYMENT_REPORT_SUCCESS':
      if (!action.payload.data.error) {
        action.meta.previousAction.callback(action.payload.data);
      }

      return {
        ...state,
        fetching: false,
        message: (action.payload.data.error && action.payload.data.error.text) || null
      }
    case 'GET_ORG_SALES_REPORT_FAILURE':
    case 'GET_ORG_PAYMENT_REPORT_FAILURE':
      return {
        ...state,
        fetching: false,
        message: (action.payload.data.error && action.payload.data.error.text) || null
      }

    case GET_PATIENTS_REPORT_INIT:{
     return {
       ...state,
       patientsReportInit: { fetching: true, data: null, error: null }
     };
    }

    case GET_PATIENTS_REPORT_INIT_SUCCESS:{
      return {
        ...state,
        patientsReportInit: { fetching: false, data: action.payload.data, error: null }
      };
     }

     case GET_PATIENTS_REPORT_INIT_FAILURE:{
      return {
        ...state,
        patientsReportInit: {
          fetching: false,
          data: null,
          error: action.error?.response?.data?.message || action.error.message
        }
      };
     }
     case GET_PATIENTS_REPORT_INIT_INTERNAL_ERROR:{
      return {
        ...state,
        patientsReportInit: {
          fetching: false,
          data: null,
          error: action.payload
        }
      };
     }

     case GET_PATIENTS_REPORT_INIT_RESET:{
      return {
        ...state,
        patientsReportInit: { fetching: false, data: null, error: null }
      };
     }
    default:
      return state
  }
}

export default organisationsReducer;
